import { Link, useLocation } from "react-router-dom";
import { Menu } from "antd";
import { UnorderedListOutlined } from "@ant-design/icons";
import { useGlobalState } from "./global";

interface MenuItem {
    id: string;
    menuName: string;
    icon: string | null;
    parentId: string | null;
    sortNum: number | null;
    path: string | null;
    component: string | null;
    query: string | null;
    routeName: string | null;
    remark: string | null;
    hiddenFlag: boolean | null;
    children: MenuItem[];
}

// 递归渲染菜单项
const renderMenuItems = (items: MenuItem[]) => {
    // 过滤掉 hiddenFlag 为 true 的项目
    const visibleItems = items.filter(item => item.hiddenFlag !== true);

    return visibleItems.map(item => {
        const icon = item.icon ? <UnorderedListOutlined /> : null;

        if (item.children && item.children.length > 0) {
            // 对子菜单也进行过滤
            const visibleChildren = item.children.filter(child => child.hiddenFlag !== true);
            if (visibleChildren.length === 0) {
                // 如果没有可见的子菜单，则只渲染普通菜单项（如果本身是可见的）
                const content = (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>{item.menuName}</span>
                    </div>
                );

                return (
                    <Menu.Item key={item.id} icon={icon}>
                        {item.path ? <Link to={item.path}>{content}</Link> : content}
                    </Menu.Item>
                );
            }

            return (
                <Menu.SubMenu
                    key={item.id}
                    title={item.menuName}
                    icon={icon}
                >
                    {renderMenuItems(visibleChildren)}
                </Menu.SubMenu>
            );
        }

        const content = (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>{item.menuName}</span>
            </div>
        );

        return (
            <Menu.Item key={item.id} icon={icon}>
                {item.path ? <Link to={item.path}>{content}</Link> : content}
            </Menu.Item>
        );
    });
};

// 获取指定路径下的二级菜单项
const getSecondLevelMenus = (key: string, items: MenuItem[]): MenuItem[] => {
    const topLevelItem = items.find(item => item.path === key);
    return topLevelItem?.children.filter(child => child.hiddenFlag !== true) ?? [];
};

// 渲染分组菜单
const renderGroupedMenus = (key: string, items: MenuItem[]) => {
    // 获取所有二级菜单
    const secondLevelMenus = getSecondLevelMenus(key, items);

    // 按分组组织二级菜单
    const groupedMenus: Record<string, { title: string; items: MenuItem[] }> = {};

    secondLevelMenus.forEach(secondLevelItem => {
        // 查找该二级菜单对应的一级菜单
        const parentMenu = items.find(topLevelItem =>
            topLevelItem.children?.some(child => child.id === secondLevelItem.id)
        );

        // 分组标题：二级菜单的routeName，不存在则用一级菜单的menuName
        const groupTitle = secondLevelItem.routeName || (parentMenu?.menuName ?? '默认分组');
        const groupId = groupTitle;

        if (!groupedMenus[groupId]) {
            groupedMenus[groupId] = {
                title: groupTitle,
                items: []
            };
        }

        groupedMenus[groupId].items.push(secondLevelItem);
    });

    // 渲染分组
    return Object.entries(groupedMenus).map(([groupId, group]) => (
        <Menu.ItemGroup key={groupId} title={group.title}>
            {renderMenuItems(group.items)}
        </Menu.ItemGroup>
    ));
};

function SideMenu() {
    const location = useLocation();
    const { permission } = useGlobalState();

    const fullPathName = location.pathname;
    const key = "/" + (fullPathName.split("/")[1] || '');

    // 递归查找匹配当前路径的菜单项 ID
    const findSelectedMenuId = (items: MenuItem[], path: string): string | null => {
        for (const item of items) {
            if (item.path === path) {
                return item.id;
            }
            if (item.children && item.children.length > 0) {
                const found = findSelectedMenuId(item.children, path);
                if (found) return found;
            }
        }
        return null;
    };

    // 获取当前选中的菜单项 ID
    const selectedMenuId = findSelectedMenuId(permission, fullPathName);
    const selectedKeys = selectedMenuId ? [selectedMenuId] : [];

    return (
        <Menu
            style={{ width: 256 }}
            selectedKeys={selectedKeys}
            mode="inline"
        >
            {renderGroupedMenus(key, permission)}
        </Menu>
    );
}

export default SideMenu;